import { CameraPhotos, PhotoType } from '@EcamModel/model';
import { appConfig } from '@configs/index';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Dialog, IconButton, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { mappingLabelPhotosType } from '@pages/manual-create-pcn/configs';
import color from '@theme/Colors';
import { formatDateByTimeZone } from 'src/helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { warpedBoxToBBox } from '@pages/ecam-v2/utils/bbox-utils';
import {
    bboxToRect,
    PcVehicleContext,
    findPcVehicleInPhoto,
    PcVehicleInPhoto,
} from '../utils/PcVehicleBbox';
import { TransformWrapperComponent, findVehicleWarpedBox, keyToPhotoType } from './EvidencePhotoGroup';
import { EvidenceTag } from './LivePhotoThumbnail';
import { convertToCropArea } from './PopUpPlateEditing';
import React from 'react';

type GeoZonePoint = { x: number; y: number; geoZoneId: number | undefined };

type Props = {
    open: boolean;
    onClose: (lastIndex: number) => void;
    items: CameraPhotos[];
    initialIndex: number;
    hasMore: boolean;
    onLoadMore: () => void;
    onLoadPrev?: () => Promise<number>;
    timezone: string;
    prevGeoZones?: GeoZonePoint[][];
    geoZoneDisplayWidth: number;
    evidenceTagMap?: Map<number, EvidenceTag[]>;
    vehicleContext?: PcVehicleContext;
    canSelectAs?: (photoType: PhotoType, item: CameraPhotos) => boolean;
    onSelectAsEvidence?: (photoType: PhotoType, item: CameraPhotos) => void;
    onSaveAdjustment?: (dataUrl: string, item: CameraPhotos) => void;
};

// ─── preview dialog ──────────────────────────────────────────────────────────
export default function LivePhotoPreviewDialog({
    open,
    onClose,
    items,
    initialIndex,
    hasMore,
    onLoadMore,
    onLoadPrev,
    timezone,
    prevGeoZones,
    geoZoneDisplayWidth,
    evidenceTagMap,
    vehicleContext,
    canSelectAs,
    onSelectAsEvidence,
    onSaveAdjustment,
}: Props) {
    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

    useEffect(() => {
        if (open) setCurrentIndex(initialIndex);
    }, [open, initialIndex]);

    // ─── YouTube-like auto-hide overlays ─────────────────────────────
    const OVERLAY_IDLE_MS = 1500;
    const [showOverlays, setShowOverlays] = useState(true);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const resetOverlayTimer = useCallback(() => {
        setShowOverlays(true);
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setShowOverlays(false), OVERLAY_IDLE_MS);
    }, []);

    useEffect(() => {
        if (!open) {
            setShowOverlays(true);
            clearTimeout(hideTimerRef.current);
            return;
        }
        resetOverlayTimer();
        return () => clearTimeout(hideTimerRef.current);
    }, [open, resetOverlayTimer]);

    const handleMouseMove = useCallback(() => {
        resetOverlayTimer();
    }, [resetOverlayTimer]);

    const handleMouseLeave = useCallback(() => {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setShowOverlays(false), 800);
    }, []);

    const handleMouseEnter = useCallback(() => {
        resetOverlayTimer();
    }, [resetOverlayTimer]);

    const hasPrev = currentIndex > 0 || !!onLoadPrev;
    const hasNext = currentIndex < items.length - 1 || hasMore;

    const goPrev = useCallback(() => {
        const wasAtStart = currentIndex === 0;

        if (!wasAtStart) {
            setCurrentIndex((i) => i - 1);
        }

        if (currentIndex <= 2 && onLoadPrev) {
            onLoadPrev().then((prependCount) => {
                if (prependCount > 0) {
                    if (wasAtStart) {
                        setCurrentIndex(prependCount - 1);
                    } else {
                        setCurrentIndex((i) => i + prependCount);
                    }
                }
            });
        }
    }, [currentIndex, onLoadPrev]);

    const goNext = useCallback(() => {
        setCurrentIndex((i) => {
            const next = i + 1;
            // trigger load-more when reaching the last 2 items
            if (next >= items.length - 2) {
                onLoadMore();
            }
            return Math.min(items.length - 1, next);
        });
    }, [items.length, onLoadMore]);

    // ─── Right-click context menu for evidence selection ─────────────
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const contextMenuRef = useRef(contextMenu);
    contextMenuRef.current = contextMenu;
    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!onSelectAsEvidence) return;
        setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
    };
    const handleCloseContextMenu = () => setContextMenu(null);

    // ─── Transform wrapper ref (zoom / pan / space-to-crop controls) ───
    const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
    const displayCropAreaRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
    const displayDimensionsRef = useRef({ width: 0, height: 0 });
    const isCropZoomedRef = useRef(false);
    const currentCropAreaIndexRef = useRef(-1);
    const allCropAreasRef = useRef<{ x: number; y: number; width: number; height: number }[]>([]);

    const focusCropArea = useCallback(() => {
        const allCropAreas = allCropAreasRef.current;
        const { width: dw, height: dh } = displayDimensionsRef.current;
        if (!allCropAreas.length || !transformWrapperRef.current) return;

        if (isCropZoomedRef.current) {
            const nextIndex = currentCropAreaIndexRef.current + 1;
            if (nextIndex < allCropAreas.length) {
                currentCropAreaIndexRef.current = nextIndex;
                displayCropAreaRef.current = allCropAreas[nextIndex];
                const cropArea = allCropAreas[nextIndex];
                const cx = cropArea.x + cropArea.width / 2;
                const cy = cropArea.y + cropArea.height / 2;
                const fitScaleX = dw / (cropArea.width * 2);
                const fitScaleY = dh / (cropArea.height * 2);
                const targetScale = Math.min(fitScaleX, fitScaleY, 6);
                const rawPosX = dw / 2 - cx * targetScale;
                const rawPosY = dh / 2 - cy * targetScale;
                const contentW = dw * targetScale;
                const contentH = dh * targetScale;
                const minX = Math.min(0, dw - contentW);
                const maxX = Math.max(0, dw - contentW);
                const minY = Math.min(0, dh - contentH);
                const maxY = Math.max(0, dh - contentH);
                const posX = Math.min(maxX, Math.max(minX, rawPosX));
                const posY = Math.min(maxY, Math.max(minY, rawPosY));
                transformWrapperRef.current.setTransform(posX, posY, targetScale, 300);
            } else {
                transformWrapperRef.current.resetTransform();
                isCropZoomedRef.current = false;
                currentCropAreaIndexRef.current = -1;
                displayCropAreaRef.current = allCropAreas[0] || null;
            }
        } else {
            const idx = currentCropAreaIndexRef.current >= 0 ? currentCropAreaIndexRef.current : 0;
            const cropArea = allCropAreas[idx] || allCropAreas[0];
            if (!cropArea) return;
            currentCropAreaIndexRef.current = idx;
            displayCropAreaRef.current = cropArea;
            const cx = cropArea.x + cropArea.width / 2;
            const cy = cropArea.y + cropArea.height / 2;
            const fitScaleX = dw / (cropArea.width * 2);
            const fitScaleY = dh / (cropArea.height * 2);
            const targetScale = Math.min(fitScaleX, fitScaleY, 6);
            const rawPosX = dw / 2 - cx * targetScale;
            const rawPosY = dh / 2 - cy * targetScale;
            const contentW = dw * targetScale;
            const contentH = dh * targetScale;
            const minX = Math.min(0, dw - contentW);
            const maxX = Math.max(0, dw - contentW);
            const minY = Math.min(0, dh - contentH);
            const maxY = Math.max(0, dh - contentH);
            const posX = Math.min(maxX, Math.max(minX, rawPosX));
            const posY = Math.min(maxY, Math.max(minY, rawPosY));
            transformWrapperRef.current.setTransform(posX, posY, targetScale, 300);
            isCropZoomedRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        const PAN_STEP = 50;

        const handler = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            const isArrow = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key);
            if (e.ctrlKey && isArrow && transformWrapperRef.current) {
                e.preventDefault();
                e.stopImmediatePropagation();
                const { positionX, positionY, scale } = transformWrapperRef.current.instance.transformState;
                const { width: dw, height: dh } = displayDimensionsRef.current;
                const dx = e.key === 'ArrowLeft' ? PAN_STEP : e.key === 'ArrowRight' ? -PAN_STEP : 0;
                const dy = e.key === 'ArrowUp' ? PAN_STEP : e.key === 'ArrowDown' ? -PAN_STEP : 0;
                const contentW = dw * scale;
                const contentH = dh * scale;
                const minX = Math.min(0, dw - contentW);
                const maxX = Math.max(0, dw - contentW);
                const minY = Math.min(0, dh - contentH);
                const maxY = Math.max(0, dh - contentH);
                const newX = Math.min(maxX, Math.max(minX, positionX + dx));
                const newY = Math.min(maxY, Math.max(minY, positionY + dy));
                transformWrapperRef.current.setTransform(newX, newY, scale, 0);
                return;
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                e.stopImmediatePropagation();
                goPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopImmediatePropagation();
                goNext();
            } else if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                e.stopImmediatePropagation();
                transformWrapperRef.current?.zoomIn(0.5);
            } else if (e.key === '-') {
                e.preventDefault();
                e.stopImmediatePropagation();
                transformWrapperRef.current?.zoomOut(0.5);
            } else if (e.key === '0') {
                e.preventDefault();
                e.stopImmediatePropagation();
                transformWrapperRef.current?.resetTransform();
                isCropZoomedRef.current = false;
                currentCropAreaIndexRef.current = -1;
            } else if (e.key === ' ') {
                e.preventDefault();
                e.stopImmediatePropagation();
                focusCropArea();
            } else if (['1', '2', '3', '4'].includes(e.key) && evidenceTagMap && !contextMenuRef.current) {
                e.preventDefault();
                e.stopImmediatePropagation();
                const targetLabel = mappingLabelPhotosType[keyToPhotoType[e.key]];
                const idx = items.findIndex((item) => {
                    if (item.Id == null) return false;
                    const tags = evidenceTagMap.get(item.Id);
                    return tags?.some((tag) => tag.label === targetLabel);
                });
                if (idx >= 0) {
                    setCurrentIndex(idx);
                }
            } else if (onSelectAsEvidence) {
                const currentItem = items[currentIndex];
                if (!currentItem) return;
                const kbdTags = currentItem.Id != null ? evidenceTagMap?.get(currentItem.Id) : undefined;
                const isAlreadyFullPlate = kbdTags?.some((t) => t.label === 'Full plate');

                const key = e.key;
                if (key === '1' && (canSelectAs ? canSelectAs(PhotoType.Entry, currentItem) : true) && !kbdTags?.some((t) => t.label === 'Entry')) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    onSelectAsEvidence(PhotoType.Entry, currentItem);
                    setContextMenu(null);
                } else if (key === '2' && (canSelectAs ? canSelectAs(PhotoType.Validation, currentItem) : true) && !kbdTags?.some((t) => t.label === 'Validation')) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    onSelectAsEvidence(PhotoType.Validation, currentItem);
                    setContextMenu(null);
                } else if (key === '3' && (canSelectAs ? canSelectAs(PhotoType.Exit, currentItem) : true) && !kbdTags?.some((t) => t.label === 'Exit')) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    onSelectAsEvidence(PhotoType.Exit, currentItem);
                    setContextMenu(null);
                } else if (!isAlreadyFullPlate && key === '4') {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    onSelectAsEvidence(PhotoType.UnobstructedPlate, currentItem);
                    setContextMenu(null);
                }
            }
        };
        window.addEventListener('keydown', handler, true); // capture phase
        return () => window.removeEventListener('keydown', handler, true);
    }, [open, goPrev, goNext, items, evidenceTagMap, currentIndex, onSelectAsEvidence]);

    // ─── Konva image state ─────────────────────────────────────────────
    const [imgLoading, setImgLoading] = useState(true);
    const [image, setImage] = useState<HTMLImageElement>();
    const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
    const stageRef = useRef<any>(null);

    const item = items[currentIndex];
    // CameraPhotos has BlobName directly
    const blobUrl = item ? `${appConfig.gateway.blobURL}${item.BlobName}` : '';

    useEffect(() => {
        if (!open || !blobUrl) {
            setImgLoading(false);
            return;
        }

        const cache = imageCacheRef.current;
        const cached = cache.get(blobUrl);
        if (cached && cached.complete && cached.naturalWidth > 0) {
            setImage(cached);
            setImgLoading(false);
            return;
        }

        setImgLoading(true);
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = blobUrl;

        img.onload = () => {
            cache.set(blobUrl, img);
            setImage(img);
            setImgLoading(false);
        };

        img.onerror = () => {
            setImgLoading(false);
        };
    }, [open, blobUrl]);

    // Preload adjacent photos
    useEffect(() => {
        if (!open) return;
        const cache = imageCacheRef.current;
        const PRELOAD_RANGE = 3;
        for (let offset = -PRELOAD_RANGE; offset <= PRELOAD_RANGE; offset++) {
            if (offset === 0) continue;
            const idx = currentIndex + offset;
            if (idx >= 0 && idx < items.length) {
                // CameraPhotos has BlobName directly
                const blob = items[idx].BlobName;
                if (blob) {
                    const url = `${appConfig.gateway.blobURL}${blob}`;
                    if (!cache.has(url)) {
                        const img = new window.Image();
                        img.crossOrigin = 'anonymous';
                        img.src = url;
                        img.onload = () => {
                            cache.set(url, img);
                        };
                    }
                }
            }
        }
    }, [open, currentIndex, items]);

    if (!item) return null;

    const isCurrentPhotoEvidence = item.Id != null && evidenceTagMap?.has(item.Id);

    const displayWidth = geoZoneDisplayWidth || 1280;
    const displayHeight = displayWidth * (9 / 16);
    displayDimensionsRef.current = { width: displayWidth, height: displayHeight };

    const rootImageWidth = image?.width || 1920;
    const scaleX = displayWidth / rootImageWidth;
    const rootImageHeight = image?.height || 1080;
    const scaleY = displayHeight / rootImageHeight;
    const scale = Math.min(scaleX, scaleY);

    const displayScaleSize = rootImageWidth / displayWidth;

    // Only the PC's vehicle gets a bbox (same rule as ReviewDetail). Without a context
    // (evidence-card fallback preview) each item already holds just its evidence vehicle.
    const pcVehicles: PcVehicleInPhoto[] = vehicleContext
        ? [findPcVehicleInPhoto(item, vehicleContext)].filter((v): v is PcVehicleInPhoto => !!v)
        : (item.CameraPhotoVehicles ?? []).map((v) => {
              const box = findVehicleWarpedBox(v);
              return { vehicle: v, vehicleBbox: box ? warpedBoxToBBox(box) : null };
          });
    const vehiclesToShow = pcVehicles.map((v) => v.vehicle);

    // Space-key zoom keeps the old behaviour: evidence vehicle(s) if the photo has any, else every
    // vehicle in the photo — even those without a drawn bbox.
    const allVehicles = item.CameraPhotoVehicles ?? [];
    const evidenceVehicles = allVehicles.filter(
        (v) => v.Id != null && !!vehicleContext?.evidenceVehicleIds.has(v.Id)
    );
    const zoomVehicles = evidenceVehicles.length ? evidenceVehicles : allVehicles;

    // Build list of all crop areas from vehicles (for Space key cycling)
    const allCropAreas = zoomVehicles
        .map((v) =>
            convertToCropArea({
                X1: v.X1 / displayScaleSize,
                X2: v.X2 / displayScaleSize,
                X3: v.X3 / displayScaleSize,
                X4: v.X4 / displayScaleSize,
                Y1: v.Y1 / displayScaleSize,
                Y2: v.Y2 / displayScaleSize,
                Y3: v.Y3 / displayScaleSize,
                Y4: v.Y4 / displayScaleSize,
            })
        )
        .filter(
            (ca): ca is { x: number; y: number; width: number; height: number } =>
                ca !== null && ca.width > 0 && ca.height > 0
        );
    allCropAreasRef.current = allCropAreas;

    // Reset crop area index when photo changes
    const currentCropAreaIndex = currentCropAreaIndexRef.current;
    displayCropAreaRef.current =
        currentCropAreaIndex >= 0 && currentCropAreaIndex < allCropAreas.length
            ? allCropAreas[currentCropAreaIndex]
            : allCropAreas[0] || null;

    const vehicleBBoxes = pcVehicles.flatMap(({ vehicle: v, vehicleBbox }) => {
        if (!vehicleBbox) return [];
        const r = bboxToRect(vehicleBbox);
        return [
            {
                x: r.x / displayScaleSize,
                y: r.y / displayScaleSize,
                width: r.width / displayScaleSize,
                height: r.height / displayScaleSize,
                plate: v.Plate || v.RawPlate || '',
                color: item.Id != null ? evidenceTagMap?.get(item.Id)?.[0]?.color : undefined,
            },
        ];
    });

    const overlayFadeSx = {
        opacity: showOverlays ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: showOverlays ? 'auto' : 'none',
    } as const;

    const navBtnSx = {
        position: 'absolute' as const,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        bgcolor: 'rgba(0,0,0,0.5)',
        color: '#fff',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
        ...overlayFadeSx,
    };

    const firstTag = item.Id != null ? evidenceTagMap?.get(item.Id)?.[0] : null;
    const hasVehicle = item.CameraPhotoVehicles?.some((v) => v.Id != null) ?? false;

    return (
        <Dialog
            open={open}
            onClose={() => onClose(currentIndex)}
            PaperProps={{
                onContextMenu: handleContextMenu,
                onMouseMove: handleMouseMove,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                onDoubleClick: focusCropArea,
                sx: {
                    maxWidth: 'none',
                    width: displayWidth,
                    height: 'fit-content',
                    overflow: 'visible',
                    position: 'relative',
                    cursor: showOverlays ? 'default' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Image area — fixed height, clips zoom/pan content */}
            <Box
                sx={{
                    position: 'relative',
                    width: displayWidth,
                    height: displayHeight,
                    overflow: 'hidden',
                    flexShrink: 0,
                }}
            >
                {hasPrev && (
                    <IconButton size="small" onClick={goPrev} sx={{ ...navBtnSx, left: 8 }}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
                {hasNext && (
                    <IconButton size="small" onClick={goNext} sx={{ ...navBtnSx, right: 8 }}>
                        <ChevronRightIcon />
                    </IconButton>
                )}

                <TransformWrapperComponent
                    transformWrapperRef={transformWrapperRef}
                    loading={imgLoading}
                    displayWidth={displayWidth}
                    displayHeight={displayHeight}
                    scale={scale}
                    scaleZoomChangeCursor={1}
                    image={image}
                    stageRef={stageRef}
                    prevGeoZones={prevGeoZones}
                    cropArea={null}
                    cameraPhotoVehicles={vehiclesToShow}
                    vehicleBoundingBoxes={vehicleBBoxes}
                    useCustomDisplayWidth
                    showZoomIn={true}
                    showZoomOut={true}
                    adjustmentEnabled={!!(item.Id != null && evidenceTagMap?.get(item.Id)?.length)}
                    initialBrightness={0}
                    initialContrast={0}
                    showOverlays={showOverlays}
                    onInteraction={resetOverlayTimer}
                    onSaveAdjustment={onSaveAdjustment ? (dataUrl) => onSaveAdjustment(dataUrl, item) : undefined}
                />
            </Box>

            {/* Info bar — sits below the image, outside the clipped area */}
            <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                    px: 1.5,
                    height: 36,
                    bgcolor: 'rgba(10,10,10,0.88)',
                    flexShrink: 0,
                    borderTop: '1px solid rgba(255,255,255,0.07)',
                }}
            >
                <Typography variant="body2" color={color.white} sx={{ flexShrink: 0 }}>
                    {currentIndex + 1} / {items.length} — {formatDateByTimeZone(item.CaptureAt, timezone)}
                </Typography>

                {firstTag && (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1,
                            py: 0.2,
                            borderRadius: '12px',
                            bgcolor: firstTag.color ? `${firstTag.color}99` : 'rgba(255,255,255,0.15)',
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: firstTag.color ?? color.white,
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="body2" color={color.white} sx={{ lineHeight: 1, fontSize: 12 }}>
                            {firstTag.label}
                        </Typography>
                    </Box>
                )}
            </Stack>

            {/* Right-click context menu for evidence selection */}
            {onSelectAsEvidence && hasVehicle && (() => {
                const dlgTags = item.Id != null ? evidenceTagMap?.get(item.Id) : undefined;
                const dlgShowEntry = (canSelectAs ? canSelectAs(PhotoType.Entry, item) : true) && !dlgTags?.some((t) => t.label === 'Entry');
                const dlgShowValidation = (canSelectAs ? canSelectAs(PhotoType.Validation, item) : true) && !dlgTags?.some((t) => t.label === 'Validation');
                const dlgShowExit = (canSelectAs ? canSelectAs(PhotoType.Exit, item) : true) && !dlgTags?.some((t) => t.label === 'Exit');
                const dlgShowFullPlate = !dlgTags?.some((t) => t.label === 'Full plate');
                if (!dlgShowEntry && !dlgShowValidation && !dlgShowExit && !dlgShowFullPlate) return null;
                return (
                    <Menu
                        open={contextMenu !== null}
                        onClose={handleCloseContextMenu}
                        anchorReference="anchorPosition"
                        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                        autoFocus={false}
                        disableAutoFocusItem
                        slotProps={{
                            paper: { sx: { minWidth: 220, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } },
                        }}
                    >
                        {dlgShowEntry && (
                            <MenuItem onClick={() => { handleCloseContextMenu(); onSelectAsEvidence(PhotoType.Entry, item); }}>
                                <ListItemText>Select as Entry Photo</ListItemText>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>1</Typography>
                            </MenuItem>
                        )}
                        {dlgShowValidation && (
                            <MenuItem onClick={() => { handleCloseContextMenu(); onSelectAsEvidence(PhotoType.Validation, item); }}>
                                <ListItemText>Select as Validation Photo</ListItemText>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>2</Typography>
                            </MenuItem>
                        )}
                        {dlgShowExit && (
                            <MenuItem onClick={() => { handleCloseContextMenu(); onSelectAsEvidence(PhotoType.Exit, item); }}>
                                <ListItemText>Select as Exit Photo</ListItemText>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>3</Typography>
                            </MenuItem>
                        )}
                        {dlgShowFullPlate && (
                            <MenuItem onClick={() => { handleCloseContextMenu(); onSelectAsEvidence(PhotoType.UnobstructedPlate, item); }}>
                                <ListItemText>Select as Full Plate Photo</ListItemText>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontSize: 12 }}>4</Typography>
                            </MenuItem>
                        )}
                    </Menu>
                );
            })()}
        </Dialog>
    );
}
